import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Form, Input, Modal, Steps } from 'antd';
import { CopyOutlined, ExportOutlined, FormOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import dynamicFormsService from '../services/dynamicFormsService';
import dynamicFormsAdminService from '../services/dynamicFormsAdminService';
import languageService from '../services/languageService';
import QuestionsBuilder from './dynamicForms/QuestionsBuilder';
import DesignEditor from './dynamicForms/DesignEditor';
import { PORTAL_ORIGIN } from '../config';
import '../styles/dynamicForms.css';

/**
 * Create a client admin with a new dynamic form, then build it: questions, then design.
 * The builder and design steps are the same editors as the form's workspace, with the
 * real form page as the preview.
 */
const DynamicFormBuilderModal = ({ visible, onClose, onSuccess }) => {
  const { message } = App.useApp();
  const [adminForm] = Form.useForm();
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [formId, setFormId] = useState(null);
  const [formName, setFormName] = useState('');
  const [slug, setSlug] = useState(null);
  const [fields, setFields] = useState([]);
  const [layout, setLayout] = useState(null);
  const [language, setLanguage] = useState(null);

  useEffect(() => {
    if (!visible) {
      setStep(0);
      setFormId(null);
      setFormName('');
      setSlug(null);
      setFields([]);
      setLayout(null);
      setLanguage(null);
      adminForm.resetFields();
    }
  }, [visible, adminForm]);

  const loadFields = useCallback(async () => {
    if (!formId) return [];
    const response = await dynamicFormsService.getFields(formId);
    const list = response.success && Array.isArray(response.fields) ? response.fields : [];
    setFields(list);
    return list;
  }, [formId]);

  const loadLayout = useCallback(async () => {
    if (!formId) return;
    const response = await dynamicFormsService.getLayout(formId);
    setLayout(response.success ? response.layout || null : null);
  }, [formId]);

  useEffect(() => {
    if (!formId) return;
    loadFields();
    loadLayout();
    languageService.getFormLanguage(formId).then((r) => r.success && setLanguage(r.form)).catch(() => {});
    dynamicFormsAdminService.getForm(formId).then((r) => setSlug(r.form?.form_slug || null)).catch(() => {});
  }, [formId, loadFields, loadLayout]);

  const createAdmin = async (values) => {
    try {
      setCreating(true);
      const response = await dynamicFormsService.registerClientAdmin({
        name: values.name,
        password: values.password,
        applicationForm: values.applicationForm,
        useDynamicForms: true, // always created with a dynamic form
      });

      if (response.status === 409) {
        const error = response.error?.toLowerCase() || '';
        if (error.includes('client admin')) {
          message.error('This username already exists. Please choose a different username.');
        } else if (error.includes('application form') || error.includes('form')) {
          message.error(response.error || 'This form name already exists. Please choose a different form name.');
        } else {
          message.error(response.error || 'A record with this information already exists');
        }
      } else if (response.success && response.clientAdmin?.dynamicFormId) {
        message.success('Client admin and form created');
        setFormId(response.clientAdmin.dynamicFormId);
        setFormName(values.applicationForm);
        setStep(1);
      } else {
        message.error(response.error || 'Failed to create client admin');
      }
    } catch {
      message.error('Failed to create client admin');
    } finally {
      setCreating(false);
    }
  };

  const liveUrl = slug ? `${PORTAL_ORIGIN}/client-portal/${slug}` : null;

  const finish = () => {
    onSuccess && onSuccess();
    onClose();
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(liveUrl);
      message.success('Link copied');
    } catch {
      message.error('Could not copy the link');
    }
  };

  const steps = [
    { title: 'Client admin' },
    { title: 'Questions' },
    { title: 'Design' },
    { title: 'Done' },
  ];

  let body;
  if (step === 0) {
    body = (
      <div className="dfw-create">
        <p className="dfw-sub">A client admin can log in to see this form’s responses. The form’s address comes from its name.</p>
        <Form form={adminForm} layout="vertical" onFinish={createAdmin} requiredMark={false}>
          <Form.Item
            name="name"
            label="Admin username"
            rules={[
              { required: true, message: 'Please enter admin username' },
              { min: 3, message: 'Username must be at least 3 characters' },
            ]}
          >
            <Input prefix={<UserOutlined aria-hidden="true" />} placeholder="e.g. newclient26" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="applicationForm"
            label="Form name"
            extra="Shown in the form’s address, e.g. client-portal/newclient"
            rules={[{ required: true, message: 'Please enter form name' }]}
          >
            <Input prefix={<FormOutlined aria-hidden="true" />} placeholder="e.g. NewClient" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined aria-hidden="true" />} placeholder="At least 8 characters" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined aria-hidden="true" />} placeholder="Type the password again" autoComplete="new-password" />
          </Form.Item>
        </Form>
      </div>
    );
  } else if (step === 1) {
    body = (
      <QuestionsBuilder
        formId={formId}
        fields={fields}
        onFieldsChange={loadFields}
        language={language}
        layout={layout}
        liveUrl={liveUrl}
      />
    );
  } else if (step === 2) {
    body = (
      <DesignEditor
        formId={formId}
        layout={layout}
        onLayoutChange={loadLayout}
        language={language}
        fields={fields}
        liveUrl={liveUrl}
      />
    );
  } else {
    body = (
      <div className="dfw-create dfw-create--done">
        <h3 className="dfw-editor__title">{formName} is ready</h3>
        <p className="dfw-sub">
          {fields.length} {fields.length === 1 ? 'question' : 'questions'}{layout ? ' and a design' : ', no design saved yet'}. The form is open, so anyone with the link can send it.
          You can change everything later from the client admin page.
        </p>
        {liveUrl && (
          <div className="dfw-rail__link dfw-create__link">
            <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="dfw-rail__url">{liveUrl.replace(/^https?:\/\//, '')}</a>
            <span className="dfw-rail__linkbtns">
              <Button size="small" type="text" icon={<CopyOutlined aria-hidden="true" />} onClick={copyLink} aria-label="Copy the form link" />
              <Button size="small" type="text" icon={<ExportOutlined aria-hidden="true" />} href={liveUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the live form" />
            </span>
          </div>
        )}
      </div>
    );
  }

  const footer = (
    <div className="dfw-create__footer">
      {step > 1 && step < 3 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
      <span className="dfw-grow" />
      {step === 0 && (
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => adminForm.submit()} loading={creating}>Create and continue</Button>
        </>
      )}
      {step === 1 && <Button type="primary" onClick={() => setStep(2)}>Next: design</Button>}
      {step === 2 && <Button type="primary" onClick={() => setStep(3)}>{layout ? 'Next' : 'Skip for now'}</Button>}
      {step === 3 && <Button type="primary" onClick={finish}>Finish</Button>}
    </div>
  );

  return (
    <Modal
      title="Create dynamic form"
      open={visible}
      onCancel={step === 0 ? onClose : finish}
      footer={footer}
      width={step === 0 || step === 3 ? 560 : 'min(1440px, 96vw)'}
      style={{ top: 24 }}
      rootClassName="do-scope dfw-create-modal"
      destroyOnHidden
      maskClosable={false}
    >
      <Steps current={step} items={steps} size="small" className="dfw-create__steps" />
      {body}
    </Modal>
  );
};

export default DynamicFormBuilderModal;
